import { useState, useEffect, useMemo } from "react";
import {
	Loader2,
	Plus,
	Pencil,
	Trash2,
	Link2,
	Paperclip,
	MessageSquare,
	MessagesSquare,
	ListChecks,
	Check,
	Link,
	CornerLeftUp,
	Bell,
	BellRing,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
	Dialog,
	DialogContent,
	DialogClose,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
	ProjectDescriptionEditor,
	ProjectDescriptionPreview,
} from "@/components/projects/project-description";
import { projectDescriptionText } from "@/components/projects/project-description-utils";
import { PermissionGate } from "@/components/PermissionGate";
import { ChecklistSection } from "./task-detail/checklist/ChecklistSection";
import { GithubSection } from "./task-detail/github/GithubSection";
import { TaskHistorySection } from "./task-detail/history/TaskHistorySection";
import { usePermission } from "@/hooks/usePermission";
import { useApiSWR } from "@/hooks/useApiSWR";
import { useWorkspaceFeatureFlags } from "@/hooks/useWorkspaceFeatureFlags";
import { type Project } from "@/services/project.service";
import type { WorkflowStage } from "@/services/workflow-stage.service";
import { SYSTEM_STAGES } from "./system-stages";
import { StageChip, StageDot } from "./StageChip";
import {
	type ApiTaskStatus,
	type UpdateTaskPayload,
} from "@/services/task.service";
import { type Profile } from "@/services/profile.service";
import { listSprints, type Sprint } from "@/services/sprint.service";
import {
	listTaskSubStatuses,
	type TaskSubStatus,
} from "@/services/task-sub-status.service";
import { type UiTask, allAssignees } from "./types";
import { TaskAttachments } from "./TaskAttachments";
import { TaskComments } from "./TaskComments";
import {
	InlineTitle,
	InlineDescription,
	InlinePriority,
	InlineTaskType,
	InlineAssignee,
	InlineDueDate,
	InlineEstimatedTime,
	InlineLogTime,
	InlineTags,
	InlineSprint,
	InlineProject,
} from "./task-detail";
import { CalendarButton } from "./task-detail/CalendarButton";
import { EstimateHistoryPopover } from "./task-detail/EstimateHistoryPopover";
import {
	getSubscription,
	subscribe as subscribeTask,
	unsubscribe as unsubscribeTask,
} from "@/services/task-subscription.service";
function shortTaskRef(task: UiTask, project?: Project): string {
	const prefix = project?.name
		? project.name
				.split(/\s+/)
				.map((w) => w[0])
				.join("")
				.slice(0, 4)
				.toUpperCase()
		: "TASK";
	return `${prefix}-${task.id.slice(0, 4).toUpperCase()}`;
}

interface Props {
	task: UiTask | null;
	projects: Project[];
	profiles?: Profile[];
	allTasks?: UiTask[];
	stages?: WorkflowStage[];
	onClose: () => void;
	onSaveNotes?: (task: UiTask, notes: string) => Promise<void>;
	onUpdate?: (task: UiTask, payload: UpdateTaskPayload) => Promise<void>;
	onChangeStatus?: (task: UiTask, status: ApiTaskStatus) => Promise<void>;
	onAddChild?: (parent: UiTask) => void;
	onOpenTask?: (task: UiTask) => void;
	onOpenParent?: (parentId: string) => void;
	onEdit?: (task: UiTask) => void;
	onDelete?: (task: UiTask) => void;
	onToggleChildDone?: (child: UiTask) => Promise<void>;
}

export function TaskDetailDialogV2({
	task,
	projects,
	profiles = [],
	allTasks = [],
	stages,
	onClose,
	onSaveNotes,
	onUpdate,
	onChangeStatus,
	onAddChild,
	onOpenTask,
	onOpenParent,
	onEdit,
	onDelete,
	onToggleChildDone,
}: Props) {
	const [notes, setNotes] = useState("");
	const [saving, setSaving] = useState(false);
	const [statusSaving, setStatusSaving] = useState(false);
	const [subStatusSaving, setSubStatusSaving] = useState(false);
	const [showNotesEditor, setShowNotesEditor] = useState(false);
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const [subscribePending, setSubscribePending] = useState(false);
	const { can } = usePermission();
	const { flags } = useWorkspaceFeatureFlags();
	const canEdit = !!onUpdate && can("Create & edit tasks");
	const canEditStatus = !!onChangeStatus && can("Create & edit tasks");

	const taskId = task?.id;
	const taskProjectId = task?.project_id;

	const { data: sprintsData, isLoading: sprintsLoading } = useApiSWR<Sprint[]>(
		task ? ["sprints"] : null,
		listSprints,
	);
	const sprints = sprintsData ?? [];

	const { data: subStatusData } = useApiSWR<TaskSubStatus[]>(
		task ? ["task-sub-statuses"] : null,
		listTaskSubStatuses,
	);
	const subStatuses = useMemo(() => subStatusData ?? [], [subStatusData]);

	const { data: subscribedData, mutate: mutateSubscribed } =
		useApiSWR<boolean>(
			taskId && taskProjectId
				? ["tasks", taskId, "subscription"]
				: null,
			() => getSubscription(taskProjectId!, taskId!),
		);
	const subscribed = subscribedData ?? false;

	useEffect(() => {
		if (task) {
			setNotes(task.developer_notes ?? "");
			setShowNotesEditor(false);
			setConfirmingDelete(false);
		}
	}, [task]);

	const project = projects.find((p) => p.id === task?.project_id);
	const stageList = stages ?? SYSTEM_STAGES;
	const currentStage = task
		? stageList.find((s) => s.key === task.apiStatus)
		: undefined;

	const availableSubStatuses = useMemo(
		() =>
			task
				? subStatuses.filter((s) => s.parent_status === task.apiStatus)
				: [],
		[subStatuses, task],
	);
	const currentSubStatus = task?.sub_status
		? subStatuses.find((s) => s.key === task.sub_status)
		: undefined;

	const children = useMemo(
		() =>
			task ? allTasks.filter((t) => t.parent_task_id === task.id) : [],
		[allTasks, task],
	);

	const completedCount = children.filter(
		(c) => c.apiStatus === "done",
	).length;
	const progress = children.length
		? Math.round((completedCount / children.length) * 100)
		: 0;

	async function persist(payload: UpdateTaskPayload) {
		if (!task || !onUpdate) return;
		await onUpdate(task, payload);
	}

	async function handleSave() {
		if (!task) return;
		setSaving(true);
		try {
			if (onSaveNotes) {
				await onSaveNotes(task, notes);
			}
			setShowNotesEditor(false);
		} finally {
			setSaving(false);
		}
	}

	async function handleStatusChange(next: string) {
		if (!task || !onChangeStatus) return;
		const nextStatus = next as ApiTaskStatus;
		if (nextStatus === task.apiStatus) return;
		setStatusSaving(true);
		try {
			await onChangeStatus(task, nextStatus);
		} catch (e) {
			toast.error("Failed to update status", {
				description: (e as Error).message || "Please try again.",
			});
		} finally {
			setStatusSaving(false);
		}
	}

	// Clear sub-status when the task's status no longer matches its parent.
	useEffect(() => {
		if (!task || !task.sub_status || !onUpdate) return;
		if (subStatuses.length === 0) return;
		const sub = subStatuses.find((s) => s.key === task.sub_status);
		if (sub && sub.parent_status !== task.apiStatus) {
			onUpdate(task, { sub_status: null }).catch(() => {});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [task?.id, task?.apiStatus, task?.sub_status, subStatuses]);

	async function handleSubStatusChange(next: string) {
		if (!task || !onUpdate) return;
		const value = next || null;
		if (value === task.sub_status) return;
		setSubStatusSaving(true);
		try {
			await persist({ sub_status: value });
		} catch (e) {
			toast.error("Failed to update sub-status", {
				description: (e as Error).message || "Please try again.",
			});
		} finally {
			setSubStatusSaving(false);
		}
	}

	async function handleToggleSubscribe() {
		if (!task || subscribePending) return;
		const next = !subscribed;
		setSubscribePending(true);
		await mutateSubscribed(next, { revalidate: false }); // optimistic
		try {
			if (next) await subscribeTask(task.project_id, task.id);
			else await unsubscribeTask(task.project_id, task.id);
			toast.success(next ? "Subscribed to task" : "Unsubscribed", {
				description: next
					? "You will get this task in your digest email"
					: undefined,
			});
		} catch (e) {
			await mutateSubscribed(!next, { revalidate: false }); // revert
			toast.error("Failed to update subscription", {
				description: (e as Error).message || "Please try again.",
			});
		} finally {
			setSubscribePending(false);
		}
	}

	function handleCopyLink() {
		if (!task) return;
		const url = `${window.location.origin}/tasks?taskId=${task.id}&projectId=${task.project_id}`;
		navigator.clipboard
			.writeText(url)
			.then(() =>
				toast.success("Link copied", {
					description: "Task URL on clipboard",
				}),
			)
			.catch(() => toast.error("Failed to copy link"));
	}

	return (
		<>
			<Dialog
				open={!!task}
				onOpenChange={(isOpen) => {
					if (!isOpen) onClose();
				}}
			>
				<DialogContent className="max-w-5xl p-0 overflow-hidden">
					{/* Top bar */}
					<div className="flex items-center justify-between gap-3 border-b border-border bg-surface px-5 py-3">
						<div className="flex items-center gap-3 min-w-0">
							<DialogClose className="rounded-md p-1 text-danger hover:text-foreground hover:bg-muted-subtle"></DialogClose>
							{task && (
								<>
									<InlinePriority
										value={task.priority}
										canEdit={canEdit}
										onSave={(p) => persist({ priority: p })}
									/>
									<span className="text-sm text-muted">
										/
									</span>
									<span
										className="text-sm font-medium text-primary truncate cursor-pointer flex items-center hover:text-primary-hover gap-2"
										onClick={handleCopyLink}
										title="Click to copy task link"
									>
										{shortTaskRef(task, project)}
										<span>
											<Link className="h-4 w-4" />
										</span>
									</span>
								</>
							)}
						</div>
						<div className="flex items-center gap-2 shrink-0 pr-8">
							{task && (
								<CalendarButton
									projectId={task.project_id}
									taskId={task.id}
									taskTitle={task.title}
									estimatedTime={task.estimated_time}
								/>
							)}
							{task && onEdit && (
								<PermissionGate feature="Create & edit tasks">
									<Button
										variant="primary_outline"
										size="sm"
										className="text-foreground hidden!"
										onClick={() => onEdit(task)}
									>
										<Pencil className="h-3.5 w-3.5" />
										<span className="text-[12px]">
											Edit
										</span>
									</Button>
								</PermissionGate>
							)}
							{task && onDelete && (
								<PermissionGate feature="Create & edit tasks">
									<Button
										variant="destructive_outline"
										size="sm"
										className="text-foreground hover:text-danger"
										onClick={() =>
											setConfirmingDelete(true)
										}
									>
										<Trash2 className="h-3.5 w-3.5" />
										<span className="text-[12px]">
											Delete
										</span>
									</Button>
								</PermissionGate>
							)}
						</div>
					</div>

					{/* Body */}
					<div className="grid grid-cols-1 md:grid-cols-[1fr_300px] max-h-[80vh] overflow-hidden">
						{/* Main column */}
						<div
							data-desc-scroll
							className="overflow-y-auto px-6 py-6 space-y-7 max-h-[80vh]"
						>
							{/* Title + description */}
							<div>
								{task && (
									<InlineTitle
										value={task.title}
										canEdit={canEdit}
										onSave={(v) => persist({ title: v })}
									/>
								)}
								{task && (
									<InlineDescription
										value={task.description ?? ""}
										canEdit={canEdit}
										onSave={(v) =>
											persist({ description: v })
										}
									/>
								)}
								{task?.created_by && (
									<p className="mt-4 -mb-2 italic text-[10px] text-muted">
										Created by{" "}
										<span className="font-medium">
											{task.created_by.full_name ??
												task.created_by.email}
										</span>
									</p>
								)}
							</div>

							{/* Subtasks */}
							<section>
								<div className="flex items-center justify-between mb-3">
									<div className="flex items-center gap-2">
										<ListChecks className="h-4 w-4 text-muted" />
										<h3 className="text-sm font-semibold text-foreground">
											Subtasks
										</h3>
										<span className="text-xs text-muted">
											({children.length})
										</span>
									</div>
									{children.length > 0 && (
										<span className="text-xs font-medium text-primary">
											{progress}% Complete
										</span>
									)}
								</div>
								{children.length > 0 && (
									<div className="h-1.5 w-full rounded-full bg-muted-subtle overflow-hidden mb-3">
										<div
											className="h-full bg-primary transition-all"
											style={{ width: `${progress}%` }}
										/>
									</div>
								)}
								{children.length === 0 ? (
									<div className="rounded-lg border border-dashed border-border px-4 py-6 text-center">
										<p className="text-xs text-muted mb-2">
											No subtasks yet
										</p>
										{task && onAddChild && (
											<PermissionGate feature="Create & edit tasks">
												<Button
													variant="outline"
													size="sm"
													onClick={() =>
														onAddChild(task)
													}
												>
													<Plus className="h-3 w-3 mr-1" />
													Add subtask
												</Button>
											</PermissionGate>
										)}
									</div>
								) : (
									<div className="rounded-lg border border-border bg-muted-subtle/30 p-3 space-y-1.5">
										{children.map((child) => {
											const done =
												child.apiStatus === "done";
											return (
												<div
													key={child.id}
													className="flex items-center gap-2.5 group"
												>
													<button
														type="button"
														onClick={() =>
															onToggleChildDone?.(
																child,
															)
														}
														className={`h-4 w-4 rounded shrink-0 flex items-center justify-center border transition-colors ${
															done
																? "bg-primary border-primary text-white"
																: "border-border-strong bg-surface hover:border-primary"
														}`}
														aria-label={
															done
																? "Mark not done"
																: "Mark done"
														}
														disabled={
															!onToggleChildDone
														}
													>
														{done && (
															<Check className="h-3 w-3" />
														)}
													</button>
													<button
														type="button"
														onClick={() =>
															onOpenTask?.(child)
														}
														className={`flex-1 text-left text-sm truncate transition-colors ${
															done
																? "line-through text-muted"
																: "text-foreground hover:text-primary"
														}`}
													>
														{child.title}
													</button>
													{(() => {
														const childStage =
															stageList.find(
																(s) =>
																	s.key ===
																	child.apiStatus,
															);
														if (!childStage)
															return null;
														return (
															<span
																className="inline-flex items-center gap-1.5 text-[10px] font-medium shrink-0"
																style={{
																	color: childStage.color,
																}}
															>
																<StageDot
																	color={
																		childStage.color
																	}
																/>
																{
																	childStage.name
																}
															</span>
														);
													})()}
												</div>
											);
										})}
										{task && onAddChild && (
											<PermissionGate feature="Create & edit tasks">
												<button
													type="button"
													onClick={() =>
														onAddChild(task)
													}
													className="flex items-center gap-2 text-xs text-muted hover:text-primary pt-1.5"
												>
													<Plus className="h-3 w-3" />
													Add subtask
												</button>
											</PermissionGate>
										)}
									</div>
								)}
							</section>

							{/* Checklist */}
							{task && (
								<ChecklistSection
									projectId={task.project_id}
									taskId={task.id}
									canEdit={canEdit}
								/>
							)}

							{/* Attachments */}
							{task && (
								<section>
									<div className="flex items-center gap-2 mb-3">
										<Paperclip className="h-4 w-4 text-muted" />
										<h3 className="text-sm font-semibold text-foreground">
											Attachments
										</h3>
									</div>
									<TaskAttachments
										projectId={task.project_id}
										taskId={task.id}
										canEdit={canEdit}
									/>
								</section>
							)}

							{/* GitHub */}
							{task && (
								<GithubSection
									projectId={task.project_id}
									taskId={task.id}
									canEdit={canEdit}
								/>
							)}

							{/* Developer notes */}
							<section>
								<div className="flex items-center justify-between mb-3">
									<div className="flex items-center gap-2">
										<MessageSquare className="h-4 w-4 text-muted" />
										<h3 className="text-sm font-semibold text-foreground">
											Developer's Notes
										</h3>
									</div>
									<PermissionGate feature="Create & edit tasks">
										<Button
											variant="primary_outline"
											size="sm"
											onClick={() =>
												setShowNotesEditor((v) => !v)
											}
										>
											{showNotesEditor
												? "Cancel"
												: "Edit notes"}
										</Button>
									</PermissionGate>
								</div>
								{showNotesEditor ? (
									<>
										<ProjectDescriptionEditor
											value={notes}
											onChange={setNotes}
											placeholder="Add implementation details, technical context, or notes for the dev team..."
										/>
										{onSaveNotes && (
											<div className="flex justify-end mt-3">
												<Button
													onClick={handleSave}
													disabled={saving}
												>
													{saving && (
														<Loader2 className="h-4 w-4 animate-spin mr-2" />
													)}
													Save Notes
												</Button>
											</div>
										)}
									</>
								) : projectDescriptionText(notes) ? (
									<div className="text-sm text-foreground bg-muted-subtle/40 rounded-lg p-3">
										<ProjectDescriptionPreview
											value={notes}
										/>
									</div>
								) : (
									<p className="text-xs text-muted italic">
										No notes yet.
									</p>
								)}
							</section>

							{/* Comments */}
							{task && (
								<section>
									<div className="flex items-center gap-2 mb-3">
										<MessagesSquare className="h-4 w-4 text-muted" />
										<h3 className="text-sm font-semibold text-foreground">
											Comments
										</h3>
									</div>
									<TaskComments
										projectId={task.project_id}
										taskId={task.id}
									/>
								</section>
							)}

							{/* History */}
							{task && (
								<TaskHistorySection
									projectId={task.project_id}
									taskId={task.id}
									refreshKey={task.updated_at}
								/>
							)}
						</div>

						{/* Sidebar */}
						<aside className="border-l border-border bg-muted-subtle/30 overflow-y-auto px-5 py-6 space-y-5 max-h-[80vh]">
							{canEditStatus && task ? (
								<InlineTaskType
									value={task.task_type}
									canEdit={canEdit}
									onSave={(id) =>
										persist({ task_type_id: id })
									}
								/>
							) : (
								""
							)}
							{/* Status */}
							<div>
								<p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">
									Status
								</p>
								{canEditStatus && task ? (
									<div className="flex items-center gap-2">
										<div className="flex-1 min-w-0">
											<SearchableSelect<{ color: string }>
												value={task.apiStatus}
												onValueChange={
													handleStatusChange
												}
												disabled={statusSaving}
												size="sm"
												placeholder="Select status"
												options={stageList.map(
													(stage) => ({
														value: stage.key,
														label: stage.name,
														meta: {
															color: stage.color,
														},
													}),
												)}
												renderOption={(opt) => (
													<StageChip
														label={opt.label}
														color={
															opt.meta?.color ??
															""
														}
													/>
												)}
												renderValue={(opt) => (
													<span
														className="inline-flex items-center gap-2 font-medium"
														style={{
															color: opt.meta
																?.color,
														}}
													>
														<StageDot
															color={
																opt.meta
																	?.color ??
																""
															}
														/>
														{opt.label}
													</span>
												)}
											/>
										</div>
										{statusSaving && (
											<Loader2 className="h-3.5 w-3.5 animate-spin text-muted" />
										)}
									</div>
								) : currentStage ? (
									<span
										className="inline-flex items-center gap-2 text-xs font-medium"
										style={{ color: currentStage.color }}
									>
										<StageDot color={currentStage.color} />
										{currentStage.name}
									</span>
								) : null}
							</div>

							{/* Sub-Status */}
							{(availableSubStatuses.length > 0 ||
								currentSubStatus) && (
								<div>
									<p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">
										Sub-Status
									</p>
									{canEditStatus &&
									task &&
									availableSubStatuses.length > 0 ? (
										<div className="flex items-center gap-2">
											<div className="flex-1 min-w-0">
												<SearchableSelect<{ color: string }>
													value={task.sub_status ?? ""}
													onValueChange={
														handleSubStatusChange
													}
													disabled={subStatusSaving}
													size="sm"
													clearable
													placeholder="None"
													options={availableSubStatuses.map(
														(s) => ({
															value: s.key,
															label: s.name,
															meta: { color: s.color },
														}),
													)}
													renderOption={(opt) => (
														<StageChip
															label={opt.label}
															color={
																opt.meta?.color ??
																""
															}
														/>
													)}
													renderValue={(opt) => (
														<span
															className="inline-flex items-center gap-2 font-medium"
															style={{
																color: opt.meta
																	?.color,
															}}
														>
															<StageDot
																color={
																	opt.meta
																		?.color ??
																	""
																}
															/>
															{opt.label}
														</span>
													)}
												/>
											</div>
											{subStatusSaving && (
												<Loader2 className="h-3.5 w-3.5 animate-spin text-muted" />
											)}
										</div>
									) : currentSubStatus ? (
										<span
											className="inline-flex items-center gap-2 text-xs font-medium"
											style={{ color: currentSubStatus.color }}
										>
											<StageDot color={currentSubStatus.color} />
											{currentSubStatus.name}
										</span>
									) : null}
								</div>
							)}

							<Separator />
							{/* Parent task */}
							{task?.parent_task_id && task.parent_task && (
								<>
									<div>
										<p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">
											Parent
										</p>
										{(() => {
											const parent = task.parent_task!;
											const parentStage = stageList.find(
												(s) => s.key === parent.status,
											);
											return (
												<button
													type="button"
													onClick={() =>
														onOpenParent?.(
															task.parent_task_id ??
																"",
														)
													}
													disabled={!onOpenParent}
													className="flex w-full items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-left text-sm hover:bg-muted-subtle transition-colors disabled:opacity-60 disabled:cursor-default"
												>
													<CornerLeftUp className="h-3.5 w-3.5 text-muted shrink-0" />
													<span className="truncate flex-1 text-foreground">
														{parent.title}
													</span>
													{parentStage && (
														<span
															className="inline-flex items-center gap-1 text-[11px] font-medium shrink-0"
															style={{
																color: parentStage.color,
															}}
														>
															<StageDot
																color={
																	parentStage.color
																}
															/>
															{parent?.title}
														</span>
													)}
												</button>
											);
										})()}
									</div>
									<Separator />
								</>
							)}

							{/* Assignee */}
							<div>
								<p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">
									Assignee
								</p>
								{task && (
									<InlineAssignee
										assignee={task.assigned_to}
										additionalAssignees={
											task.additional_assignees
										}
										profiles={profiles}
										canEdit={canEdit}
										onSave={(v) =>
											persist({ assigned_to: v })
										}
										onSaveAdditional={(ids) =>
											persist({ assignee_ids: ids })
										}
									/>
								)}
							</div>

							<Separator />

							{/* Project + Sprint */}
							<div className="space-y-3">
								<div>
									<p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1">
										Project
									</p>
									{task && (
										<InlineProject
											projectId={task.project_id}
											projects={projects}
											canEdit={canEdit}
											onSave={(v) =>
												persist({ project_id: v })
											}
										/>
									)}
								</div>
								<div>
									<p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1">
										Sprint
									</p>
									{task && (
										<InlineSprint
											sprint={task.sprint}
											sprints={sprints}
											loading={sprintsLoading}
											canEdit={canEdit}
											onSave={(v) =>
												persist({ sprint_id: v })
											}
										/>
									)}
								</div>
							</div>

							<Separator />

							{/* Due date */}
							<div>
								<p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">
									Due Date
								</p>
								{task && (
									<InlineDueDate
										value={task.due_date}
										canEdit={canEdit}
										onSave={(v) => persist({ due_date: v })}
									/>
								)}
							</div>

							{/* Estimation */}
							<div>
								<div className="flex items-center justify-between mb-2">
									<p className="text-[10px] font-semibold uppercase tracking-wider text-muted">
										Estimation
									</p>
									{task && (
										<EstimateHistoryPopover
											projectId={task.project_id}
											taskId={task.id}
											canEdit={canEdit}
										/>
									)}
								</div>
								{task && (
									<InlineEstimatedTime
										value={
											task.active_sprint_estimated_time ??
											0
										}
										canEdit={canEdit}
										onSave={(v) =>
											persist({ estimated_time: v })
										}
									/>
								)}
							</div>

							{/* Log Time */}
							{flags.enable_time_logging && (
								<div>
									<p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">
										Log Time
									</p>
									{task && (
										<InlineLogTime
											projectId={task.project_id}
											taskId={task.id}
											assigneeIds={allAssignees(
												task,
											).map((p) => p.id)}
										/>
									)}
								</div>
							)}

							<Separator />

							{/* Tags */}
							<div>
								<p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">
									Tags
								</p>
								{task && (
									<InlineTags
										value={task.tags}
										canEdit={canEdit}
										onSave={(v) => persist({ tags: v })}
									/>
								)}
							</div>

							{/* Subscribe */}
							<Button
								variant={subscribed ? "default" : "outline"}
								className="w-full justify-start"
								onClick={handleToggleSubscribe}
								disabled={subscribePending}
							>
								{subscribePending ? (
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
								) : subscribed ? (
									<BellRing className="h-4 w-4 mr-2" />
								) : (
									<Bell className="h-4 w-4 mr-2 text-primary" />
								)}
								<span
									className={
										subscribed
											? "text-xs!"
											: "text-primary text-xs!"
									}
								>
									{subscribed
										? "Subscribed"
										: "Subscribe to this task"}
								</span>
							</Button>

							{/* Copy link */}
							<Button
								variant="outline"
								className="w-full justify-start"
								onClick={handleCopyLink}
							>
								<Link2 className="h-4 w-4 mr-2  text-primary" />
								<span className=" text-primary text-xs!">
									Copy Task Link
								</span>
							</Button>
						</aside>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog
				open={confirmingDelete && !!task}
				onOpenChange={(o) => {
					if (!o) setConfirmingDelete(false);
				}}
			>
				<DialogContent className="max-w-[420px]">
					<DialogHeader>
						<DialogTitle>Delete Task</DialogTitle>
						<DialogDescription>
							This will permanently delete{" "}
							<span className="font-medium text-foreground">
								{task?.title}
							</span>
							. This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<DialogClose asChild>
							<Button variant="outline">Cancel</Button>
						</DialogClose>
						<Button
							variant="destructive"
							className="bg-danger text-white hover:bg-danger/90"
							onClick={() => {
								if (!task || !onDelete) return;
								setConfirmingDelete(false);
								onDelete(task);
							}}
						>
							<Trash2 className="h-4 w-4 mr-1.5" />
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
